<template>
  <div>
    <span class="body-1 font-weight-bold">
      {{ $t("termDepositActions.breakDeposit.choosePayAwayAcLabel") }}
    </span>
    <v-tabs
      v-if="allowSelectingAccountType"
      show-arrows
      v-model="model.selectedBankTab"
      @change="model.selectedPayaway = null"
      :color="primaryColor"
      class="mb-4"
      data-qa="selected-bank-tab"
    >
      <v-tabs-slider :color="primaryColor" />
      <v-tab class="text-capitalize font-weight-bold">
        {{ $t("termDepositActions.listAC.internalButtonLabel") }}
      </v-tab>
      <v-tab class="text-capitalize font-weight-bold">
        {{ $t("termDepositActions.listAC.externalButtonLabel") }}
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="model.selectedBankTab">
      <v-tab-item v-for="n in 2" :key="n">
        <v-autocomplete
          v-model="model.selectedPayaway"
          filled
          :label="$t('amendTaxAccount.taxAccountAmendActions.creditEntireHint.dropdownLabel')"
          :items="n === 1 ? mappedSameBankAccounts : mappedOtherBankAccounts"
          :color="primaryColor"
          :rules="[rules.required]"
          return-object
          :hint="selectedPayAwayHint"
          persistent-hint
          :menu-props="{ transition: 'scale-transition' }"
          class="input-filled-bg"
          clearable
          data-qa="selected-payaway"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              {{ data.item.primaryLine }}
              <v-list-item-subtitle
                v-text="data.item.secondaryLine"
              />
              <v-divider />
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { formatPrimaryLine, formatSecondaryLine, formatSelectedAccount, formatSelectedHint } from '../../../util/accountUtil'

export default {
  name: 'AmendTaxSavingsSelectPayaway',
  props: {
    value: {
      type: Object,
      default: null
    },
    accountId: {
      type: String,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    sameBankAccounts: {
      type: Array,
      required: true
    },
    otherBankAccounts: {
      type: Array,
      require: true
    }
  },
  data () {
    return {
      model: {
        selectedBankTab: null,
        selectedPayaway: null
      }
    }
  },
  created () {
    this.setModelValues(this.value)
  },
  methods: {
    setModelValues (value) {
      if (!value) {
        this.model.selectedPayaway = null
        this.model.selectedBankTab = null
        return
      }

      this.model.selectedPayaway = value.otherBankAccount
        ? this.mappedOtherBankAccounts.find(i => i.payawayAccountId === value.payawayAccountId)
        : this.mappedSameBankAccounts.find(i => i.payawayAccountId === value.payawayAccountId)
      this.model.selectedBankTab = value.otherBankAccount ? 1 : 0
    }
  },
  computed: {
    rules () {
      return {
        required: (v) => Boolean(v) || this.$t('errors.common.required')
      }
    },
    mappedSameBankAccounts () {
      const allowInactiveAccounts = this.$appSettings.defaultConfigurations.allowInactiveAccountTransactionalOperations

      return (this.sameBankAccounts || [])
        .filter(i => this.accountId !== i.accountId &&
          this.currency === i.currency &&
          !['TXNA', 'TXFR', 'TXLA', 'TXSV'].includes(i.accountType) &&
          (i.accountStatus === 'ACTIVATED' || (i.accountStatus === 'INACTIVE' && allowInactiveAccounts))
        )
        .map(a => ({
          ...a,
          primaryLine: formatPrimaryLine(a),
          secondaryLine: formatSecondaryLine(a),
          text: formatSelectedAccount(a),
          value: a.accountId,
          payawayAccountId: a.accountId,
          otherBankAccount: false
        }))
    },
    mappedOtherBankAccounts () {
      const enableNominatedAccountFeatures = this.$appSettings.defaultConfigurations.nominatedAccounts.enableNominatedAccountFeatures
      return (this.otherBankAccounts || [])
        .filter(a => (!enableNominatedAccountFeatures || a.ownAccount) &&
          this.currency === a.currency
        )
        .map(a => ({
          ...a,
          primaryLine: a.name,
          secondaryLine: `${a.accountIdentification} - ${a.bankIdentifier}`,
          text: a.name,
          value: a.beneficiaryId,
          payawayAccountId: a.accountIdentification,
          otherBankAccount: true
        }))
    },
    isOtherBankSelected () {
      return this.model.selectedBankTab === 1
    },
    selectedPayAwayHint () {
      if (!this.model.selectedPayaway) return ''
      if (this.isOtherBankSelected) {
        return this.model.selectedPayaway.secondaryLine
      }
      return formatSelectedHint(this.model.selectedPayaway)
    },
    allowSelectingAccountType () {
      return this.$appSettings.defaultConfigurations.maturityInstruction.cashISA.allowSelectingAccountType
    }
  },
  watch: {
    'model.selectedPayaway' (newValue) {
      this.$emit('input', newValue)
    }
  }
}
</script>
